/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { ApiCodeBlock } from 'ui/molecules/ApiCodeBlock/ApiCodeBlock';

import styles from './ApiEndpointExplorer.module.scss';

import type { ApiEndpointExplorerProps } from './ApiEndpointExplorer.types';

export const ApiEndpointExplorer = ({ endpoints, className }: ApiEndpointExplorerProps) => {
  const [activeKey, setActiveKey] = useState(endpoints[0]?.key);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = itemRefs.current.indexOf(entry.target as HTMLButtonElement);
            if (index !== -1) {
              setActiveKey(endpoints[index].key);
            }
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );

    itemRefs.current.forEach((el) => {
      if (el) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [endpoints]);

  const active = endpoints.find((endpoint) => endpoint.key === activeKey) ?? endpoints[0];

  const handleClick = (index: number) => {
    setActiveKey(endpoints[index].key);
    itemRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.list} role="tablist">
        {endpoints.map((endpoint, index) => (
          <button
            key={endpoint.key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={endpoint.key === active.key}
            className={cn(styles.ep, { [styles.on]: endpoint.key === active.key })}
            onClick={() => handleClick(index)}
          >
            <span className={cn(styles.method, endpoint.method === 'GET' ? styles.get : styles.post)}>
              {endpoint.method}
            </span>

            <span className={styles.meta}>
              <span className={styles.path}>{endpoint.path}</span>
              <span className={styles.desc}>{endpoint.description}</span>
            </span>
          </button>
        ))}
      </div>

      <div className={styles.viewer}>
        <ApiCodeBlock key={active.key} filename={active.filename} codeTokens={active.codeTokens} />
      </div>
    </div>
  );
};
